import {computed, observable, reaction} from 'mobx';
import {nonenumerable} from 'core-decorators';
import moment from 'moment-timezone';

import {MIN_NUMBER_OF_BOOKINGS, NUMBER_OF_REVIEWS_PER_REQUEST} from 'spa/Components/Reviews/values';

class WorkerState {
  get selection() { return this.ctx.selection; }

  get workers() { return this.ctx.workers; }

  get workerLanguages() { return this.ctx.workerLanguages; }

  get workerPromotedReviews() { return this.ctx.workerPromotedReviews; }

  get workerReviews() { return this.ctx.workerReviews; }

  @observable filtersApplied = false;
  @observable fullReviewsList = [];
  @observable offset = 0;
  @observable sortReviews = 'createdAt-';
  @observable specialitiesFilter;
  @observable totalReviews = 0;

  @observable selectedId;

  @computed get isProfileLoading() {
    const {
      workers,
      workerLanguages,
      workerPromotedReviews,
      workerReviews,
    } = this;

    return workers.isLoading ||
      workerLanguages.isLoading ||
      workerPromotedReviews.isLoading ||
      workerReviews.isLoading;
  }

  @computed get workerDetails() {
    if (this.selectedId) {
      return this.workers.getOne(this.selectedId);
    }

    return null;
  }

  @computed get languagesDetails() {
    if (this.selectedId) {
      return this.workerLanguages.getOne(this.selectedId);
    }

    return null;
  }

  @computed get promotedReviews() {
    if (this.selectedId) {
      return this.workerPromotedReviews.getOne(this.selectedId);
    }

    return null;
  }

  @computed get reviewsList() {
    if (!this.selectedId) return null;

    setTimeout(() => {
      this.workerReviews.maybeLoadSearch({
        offset: this.offset,
        limit: NUMBER_OF_REVIEWS_PER_REQUEST,
        specialityId: this.specialitiesFilter,
        order: this.sortReviews,
        id: this.selectedId,
      }, this.selectedId);
    }, 0);
    return this.workerReviews.getSearch({
      offset: this.offset,
      limit: NUMBER_OF_REVIEWS_PER_REQUEST,
      specialityId: this.specialitiesFilter,
      order: this.sortReviews,
      id: this.selectedId,
    }, this.selectedId);
  }

  @computed get workerName() {
    const {workerDetails: {instance = {}} = {instance: {}}} = this;
    if (!instance) return null;

    return instance.name;
  }

  @computed get workerRating() {
    const {workerDetails: {instance = {}} = {instance: {}}} = this;
    if (!instance) return null;

    return instance.rating || '0';
  }

  @computed get workerRatingsBreakdown() {
    const {workerDetails: {instance = {}} = {instance: {}}} = this;
    if (!instance) return null;

    return instance.ratingsBreakdown;
  }

  /*
   * Split the paragraphs from the server response.
   */
  @computed get workerBio() {
    const {i18n} = this.ctx;
    const {workerDetails: {instance = {}} = {instance: {}}} = this;
    if (!instance) return null;

    const tbio = instance.bio && instance.bio.split('\n')
      .filter(bio => (
        bio.length > 0
      ));

    if (!tbio) {
      return [i18n._x('Sorry, No bio available', 'Therapist profile missing biography default text')];
    }

    return tbio;
  }

  @computed get workerExperience() {
    const {i18n} = this.ctx;
    const {workerDetails: {instance = {}} = {instance: {}}} = this;
    if (!instance) return null;

    if (!instance || !instance.experience) return null;
    const experience = instance.experience.replace(/[^-?0-9]+/, '');
    const years = parseInt(experience, 10);
    if (!years || years === 0) return null;

    if (instance.experience.includes('+')) {
      return i18n._n('%d+ Year', '%d+ Years', years);
    }
    return i18n._n('%d Year', '%d Years', years);
  }

  @computed get workerLanguagesInfo() {
    const {languagesDetails: {instance = {}} = {instance: {}}} = this;
    if (!instance) return null;

    const spoken = Object.keys(instance).map(lang => (
      instance[lang].name
    ));

    return (spoken && spoken.length) ? spoken.join(', ') : null;
  }

  @computed get workerSpecialities() {
    const {workerDetails: {instance = {}} = {instance: {}}} = this;

    return (instance && instance.specialities) || null;
  }

  @computed get numberOfBookings() {
    const {workerDetails: {instance = {}} = {instance: {}}} = this;

    if (!instance || !instance.numberOfBookings || instance.numberOfBookings < MIN_NUMBER_OF_BOOKINGS) return null;

    return instance.numberOfBookings;
  }

  @computed get lastBooked() {
    const {workerDetails: {instance = {}} = {instance: {}}} = this;

    if (!instance) return null;

    const {lastBooked} = instance;

    if (!lastBooked || moment().subtract(7, 'days') > moment(lastBooked)) return null;

    return moment(lastBooked).fromNow();
  }

  @computed get workerPromotedReviewsList() {
    const {promotedReviews: {instance = {}} = {instance: {}}} = this;
    if (!instance) return [];

    const reviewsArray = Object.keys(instance).map(index => (
      instance[index]
    ));

    return reviewsArray || [];
  }

  @nonenumerable
  listeners = [];

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });

    this.listeners = [
      reaction(
        () => this.reviewsList && this.reviewsList.instances,
        () => {
          if (this.reviewsList && this.reviewsList.instances && this.reviewsList.instances.length) {
            if (this.fullReviewsList && this.fullReviewsList.length && this.offset !== 0) {
              this.fullReviewsList = [...this.fullReviewsList, ...this.reviewsList.instances];
            } else {
              this.fullReviewsList = this.reviewsList.instances;
            }

            if (this.reviewsList.pagination && this.offset === 0) {
              this.totalReviews = this.reviewsList.pagination.total;
            }
          } else if (this.offset === 0 && (!this.reviewsList || !this.reviewsList.isLoading)) {
            this.fullReviewsList = [];
          }
        }, {
          name: 'Get list of reviews',
          fireImmediately: true,
        },
      ),
    ];
  }

  dispose() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }
}

export default WorkerState;



// WEBPACK FOOTER //
// ./spa/Pages/Therapists/workerState.jsx